import { FC, MouseEvent, Ref, useRef } from "react";

import { userHasGlobalAuthority } from "../../../../../shared/capabilities";
import { CourseRole, OrgRole } from "../../../../../shared/types/roles";

import { ExtendedOrgRoleType, getIconFromOrgRole } from "./UserRoleIcons";

import { InviteStatus } from "api/api-data-types";
import { serverRequestResetPassword } from "api/api-server-actions";
import { showManageEnrollmentModal } from "components/menus/ManageEnrollmentModal";
import { UserType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import {
  Menu,
  MenuDivider,
  MenuInstance,
  MenuItem,
  MenuRadioGroup,
  SubMenu,
} from "mds/components/Menu";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  CircleDisabledIcon,
  CopySendArrowIcon,
  DialSettingsIcon,
  DotMenuVerticalIcon,
  LetterIcon,
  PencilEditIcon,
  PersonSingleIcon,
  TextBooksIcon,
} from "mds/icons";
import { useAppDispatch } from "store/index";
import { computedStateActions } from "store/slices/computed";
import { toastErrorMessage, toastSuccessMessage } from "utils/alerts";

const ORG_ROLES = [OrgRole.ADMIN, CourseRole.COURSE_ADMIN, null] as const;

export type AdminUserOptionsMenuProps = {
  user: UserType;
  inviteStatus: InviteStatus;
  isDisabled?: boolean;
  currentRole: ExtendedOrgRoleType;
  onChangeRole?: (newRole: (typeof ORG_ROLES)[number]) => void;
  onImpersonate?: () => void;
  onToggleDisabled?: () => void;
  onEdit?: () => void;
};

export const AdminUserOptionsMenu: FC<AdminUserOptionsMenuProps> = ({
  user,
  inviteStatus,
  isDisabled,
  currentRole,
  onChangeRole,
  onImpersonate,
  onToggleDisabled,
  onEdit,
}) => {
  const menuRef: Ref<MenuInstance | null> = useRef(null);
  const dispatch = useAppDispatch();

  const { email, id: userId } = user;

  const handleSendInvite = async () => {
    try {
      // We send the invite instead of the password reset if the user has not accepted the eula
      await serverRequestResetPassword(email);

      // Update the user's invite status in the computed slice to reflect the pending state
      dispatch(
        computedStateActions.saveUserInviteStatuses({
          [userId]: InviteStatus.PENDING,
        }),
      );

      toastSuccessMessage(t("user_options_menu.send_invite_success"));
    } catch (error) {
      console.error("Failed to send invite:", error);
      toastErrorMessage(t("user_options_menu.send_invite_error"));
    }
  };

  const MenuButton = (
    <SquareIconButton
      aria-label={t("common.options")}
      kind="ghost"
      size="xs"
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
      }}
    >
      <DotMenuVerticalIcon />
    </SquareIconButton>
  );

  return (
    <Menu
      direction="bottom"
      instanceRef={menuRef}
      limitItemHeight={false}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      onClick={(e) => e.stopPropagation()}
    >
      {email && (
        <>
          <MenuItem
            className="h-auto"
            eventName="Org User Options - Send email"
            eventProps={{ userEmail: email, userId }}
            href={`mailto:${email}`}
            target="_blank"
            allowPropagation
          >
            <div className="flex flex-col gap-1">
              <IconText iconStart={<LetterIcon />} text={t("user_options_menu.email_user")} />
              <div className="ml-6 block">{email}</div>
            </div>
          </MenuItem>

          {inviteStatus !== InviteStatus.ACCEPTED && !isDisabled && (
            <MenuItem
              className="h-auto"
              eventName="Org User Options - Send invite"
              eventProps={{ userEmail: email, userId }}
              onClick={handleSendInvite}
            >
              <div className="flex flex-col gap-1">
                <IconText
                  iconStart={<CopySendArrowIcon />}
                  text={
                    inviteStatus === InviteStatus.NOT_SENT
                      ? t("user_options_menu.send_invite")
                      : t("user_options_menu.resend_invite")
                  }
                />
                <div className="ml-6 block">{email}</div>
              </div>
            </MenuItem>
          )}
        </>
      )}

      {onImpersonate && (
        <MenuItem
          eventName="Org User Options - Impersonate user"
          eventProps={{ userEmail: email, userId }}
          onClick={onImpersonate}
        >
          <IconText
            iconStart={<PersonSingleIcon />}
            text={t("user_options_menu.impersonate_user")}
          />
        </MenuItem>
      )}

      <MenuItem onClick={() => showManageEnrollmentModal({ userId })}>
        <IconText
          iconStart={<TextBooksIcon />}
          text={t("user_options_menu.manage_enrollment.menu_label")}
        />
      </MenuItem>

      <MenuItem
        className="justify-between"
        disabled={userHasGlobalAuthority(user)}
        eventName="Org User Options - Edit user information"
        eventProps={{ userEmail: email, userId }}
        onClick={onEdit}
      >
        <IconText
          iconStart={<PencilEditIcon />}
          text={t("user_options_menu.edit_user_info.menu_label")}
        />
        {userHasGlobalAuthority(user) && (
          <TooltipTrigger content={t("user_options_menu.edit_user_info.super_admin_disabled")} />
        )}
      </MenuItem>

      {onChangeRole && (
        <SubMenu
          disabled={userHasGlobalAuthority(user)}
          label={
            <div className="flex w-full items-center justify-between gap-1">
              <IconText
                iconStart={getIconFromOrgRole(currentRole)}
                text={t("user_options_menu.edit_role")}
              />
              {userHasGlobalAuthority(user) && (
                <TooltipTrigger
                  content={t("user_options_menu.edit_user_info.super_admin_disabled")}
                />
              )}
            </div>
          }
          position="auto"
          withArrow={!userHasGlobalAuthority(user)}
        >
          <MenuRadioGroup
            value={currentRole}
            onRadioChange={(e) => onChangeRole(e.value as (typeof ORG_ROLES)[number])}
          >
            {ORG_ROLES.map((role) => (
              <MenuItem
                eventName="Org User Options - Change user role"
                eventProps={{ userEmail: email, userId }}
                key={role}
                type="radio"
                value={role}
              >
                <IconText
                  iconStart={getIconFromOrgRole(role)}
                  text={t(`roles.org_${role ?? "other"}`)}
                />
              </MenuItem>
            ))}
          </MenuRadioGroup>

          {currentRole === CourseRole.COURSE_ADMIN && (
            <>
              <MenuDivider />
              <MenuItem
                eventName="Org User Options - Manage course admin access"
                eventProps={{ userEmail: email, userId }}
                onClick={() => onChangeRole(CourseRole.COURSE_ADMIN)}
              >
                <IconText
                  iconStart={<DialSettingsIcon />}
                  text={t("user_options_menu.manage_course_admin.title")}
                />
              </MenuItem>
            </>
          )}
        </SubMenu>
      )}

      {onToggleDisabled &&
        (isDisabled ? (
          <MenuItem
            disabled={!onToggleDisabled}
            eventName="Org User Options - Enable org user"
            eventProps={{ userEmail: email, userId }}
            onClick={onToggleDisabled}
          >
            <IconText iconStart={<PersonSingleIcon />} text={t("user_actions.enable")} />
          </MenuItem>
        ) : (
          <MenuItem
            disabled={!onToggleDisabled}
            eventName="Org User Options - Disable org user"
            eventProps={{ userEmail: email, userId }}
            onClick={onToggleDisabled}
          >
            <IconText
              iconStart={<CircleDisabledIcon className="text-red" />}
              text={t("user_actions.disable")}
            />
          </MenuItem>
        ))}
    </Menu>
  );
};
